import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Modal,
  Radio,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  Badge,
} from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import Layout from '../../layout/Layout';
import { getHeaders } from '../../utility/getHeader';
import { formatBangkokDateTime } from '../../utility/bangkokTime';
import { exportReconciliationExcel, exportReconciliationPdf } from '../../utility/ticketReconciliationExport';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `THB ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PAYMENT_COLORS = { cash: 'green', card: 'blue', qr: 'purple', upi: 'geekblue', entertainment: 'orange' };

export default function TicketReconciliationReport() {
  const [dateRange, setDateRange] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('reconciliation'); // 'reconciliation' or 'unscanned'
  const [unscannedTickets, setUnscannedTickets] = useState([]);
  const [scannedTickets, setScannedTickets] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [paymentView, setPaymentView] = useState('total'); // 'total' or 'daily'
  const [unscannedLoading, setUnscannedLoading] = useState(false);
  const [selectedBillDetails, setSelectedBillDetails] = useState(null);
  const [billDetailsModal, setBillDetailsModal] = useState(false);
  const [reportData, setReportData] = useState({
    summary: null,
    daily: [],
    payment_breakdown: [],
    payment_daily: [],
    table_status: { has_tracking_table: true, has_scan_logs_table: true },
  });

  const fetchReport = useCallback(async (rangeValue) => {
    const [start, end] = Array.isArray(rangeValue) ? rangeValue : [];
    const hasDateRange = Boolean(start && end && dayjs.isDayjs(start) && dayjs.isDayjs(end));

    setLoading(true);
    setError('');

    try {
      const requestConfig = {
        ...getHeaders(),
        ...(hasDateRange
          ? {
              params: {
                start_date: start.format('YYYY-MM-DD'),
                end_date: end.format('YYYY-MM-DD'),
              },
            }
          : {}),
      };

      const response = await axios.get('/reports/ticket-reconciliation', requestConfig);

      if (response.data?.success) {
        const serverFilters = response.data.filters || {};
        if (!hasDateRange && serverFilters.start_date && serverFilters.end_date) {
          setDateRange([dayjs(serverFilters.start_date), dayjs(serverFilters.end_date)]);
        }

        setReportData({
          summary: response.data.summary || null,
          daily: response.data.daily || [],
          payment_breakdown: response.data.payment_breakdown || [],
          payment_daily: response.data.payment_daily || [],
          table_status: response.data.table_status || {
            has_tracking_table: true,
            has_scan_logs_table: true,
          },
        });
      } else {
        setReportData({
          summary: null,
          daily: [],
          payment_breakdown: [],
          payment_daily: [],
          table_status: { has_tracking_table: true, has_scan_logs_table: true },
        });
        setError(response.data?.message || 'Failed to load reconciliation report');
      }
    } catch (err) {
      setReportData({
        summary: null,
        daily: [],
        payment_breakdown: [],
        payment_daily: [],
        table_status: { has_tracking_table: true, has_scan_logs_table: true },
      });
      setError(err?.response?.data?.message || 'Failed to load reconciliation report');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnscannedTickets = useCallback(async (rangeValue) => {
    const [start, end] = Array.isArray(rangeValue) ? rangeValue : [];
    const hasDateRange = Boolean(start && end && dayjs.isDayjs(start) && dayjs.isDayjs(end));

    setUnscannedLoading(true);
    try {
      const requestConfig = {
        ...getHeaders(),
        ...(hasDateRange
          ? {
              params: {
                start_date: start.format('YYYY-MM-DD'),
                end_date: end.format('YYYY-MM-DD'),
              },
            }
          : {}),
      };

      const response = await axios.get('/reports/unscanned-tickets', requestConfig);
      if (response.data?.success) {
        setUnscannedTickets(response.data.data || []);
        setScannedTickets(response.data.scanned || []);
      } else {
        setUnscannedTickets([]);
        setScannedTickets([]);
      }
    } catch (err) {
      console.error('Failed to load unscanned tickets:', err);
      setUnscannedTickets([]);
      setScannedTickets([]);
    } finally {
      setUnscannedLoading(false);
    }
  }, []);

  // Load the report and the code-difference details together so the badge count is always correct.
  const loadAll = useCallback(async (rangeValue) => {
    await fetchReport(rangeValue);
    await fetchUnscannedTickets(rangeValue);
  }, [fetchReport, fetchUnscannedTickets]);

  useEffect(() => {
    loadAll(null);
  }, [loadAll]);

  const fetchBillDetails = useCallback(async (billNumber) => {
    try {
      const response = await axios.get(`/reports/bill-details/${billNumber}`, getHeaders());
      if (response.data?.success) {
        setSelectedBillDetails(response.data.data);
        setBillDetailsModal(true);
      }
    } catch (err) {
      console.error('Failed to load bill details:', err);
    }
  }, []);

  const summary = reportData.summary || {
    billed_amount: 0,
    scanned_amount: 0,
    amount_difference: 0,
    generated_codes: 0,
    unique_scanned_codes: 0,
    codes_difference: 0,
    not_found_scans: 0,
    duplicate_scans: 0,
    verified_scans: 0,
    scan_attempts: 0,
    is_amount_matched: false,
    is_codes_matched: false,
  };

  const codeColumns = useMemo(() => [
    {
      title: 'Bill Number',
      dataIndex: 'bill_number',
      key: 'bill_number',
      render: (text) => (
        <span
          style={{ cursor: 'pointer', color: '#1890ff' }}
          onClick={() => fetchBillDetails(text)}
        >
          {text}
        </span>
      ),
    },
    { title: 'Ticket ID', dataIndex: 'ticket_id', key: 'ticket_id' },
    { title: 'Barcode', dataIndex: 'barcode_value', key: 'barcode_value' },
    { title: 'Type', dataIndex: 'print_mode', key: 'print_mode', width: 100 },
    {
      title: 'Payment',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 110,
      render: (value) => <Tag color={PAYMENT_COLORS[String(value || '').toLowerCase()] || 'default'}>{String(value || '-').toUpperCase()}</Tag>,
    },
    {
      title: 'Generated On',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (value, row) => (value ? formatBangkokDateTime(value) : row.setup_date),
    },
    {
      title: 'Ticket Amount',
      dataIndex: 'ticket_amount',
      key: 'ticket_amount',
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    { title: 'Cashier', dataIndex: 'cashier_name', key: 'cashier_name', width: 120 },
  ], [fetchBillDetails]);


  const columns = useMemo(() => [
    {
      title: 'Date',
      dataIndex: 'report_date',
      key: 'report_date',
      width: 120,
    },
    {
      title: 'Bills',
      dataIndex: 'bills_count',
      key: 'bills_count',
      align: 'right',
      width: 90,
    },
    {
      title: 'Billed Amount',
      dataIndex: 'billed_amount',
      key: 'billed_amount',
      align: 'right',
      render: (value) => formatCurrency(value),
      width: 150,
    },
    {
      title: 'Generated Codes',
      dataIndex: 'generated_codes',
      key: 'generated_codes',
      align: 'right',
      width: 140,
    },
    {
      title: 'Unique Scanned',
      dataIndex: 'unique_scanned_codes',
      key: 'unique_scanned_codes',
      align: 'right',
      width: 140,
    },
    {
      title: 'Scanned Amount',
      dataIndex: 'scanned_amount',
      key: 'scanned_amount',
      align: 'right',
      render: (value) => formatCurrency(value),
      width: 150,
    },
    {
      title: 'Amount Diff',
      dataIndex: 'amount_difference',
      key: 'amount_difference',
      align: 'right',
      render: (value) => {
        const color = Number(value) === 0 ? 'success' : Number(value) > 0 ? 'warning' : 'error';
        return <Tag color={color}>{formatCurrency(value)}</Tag>;
      },
      width: 140,
    },
    {
      title: 'Verified',
      dataIndex: 'verified_scans',
      key: 'verified_scans',
      align: 'right',
      width: 90,
    },
    {
      title: 'Duplicate',
      dataIndex: 'duplicate_scans',
      key: 'duplicate_scans',
      align: 'right',
      width: 95,
    },
    {
      title: 'Not Found',
      dataIndex: 'not_found_scans',
      key: 'not_found_scans',
      align: 'right',
      width: 95,
    },
    {
      title: 'Status',
      key: 'status',
      fixed: 'right',
      width: 140,
      render: (_, row) => {
        if (row.is_amount_matched && row.is_codes_matched) {
          return <Tag color="success">Matched</Tag>;
        }
        if (!row.is_amount_matched && !row.is_codes_matched) {
          return <Tag color="error">Mismatch</Tag>;
        }
        return <Tag color="warning">Partial</Tag>;
      },
    },
  ], []);

  const paymentColumns = [
    {
      title: 'Payment',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (value) => <Tag color={PAYMENT_COLORS[value] || 'default'}>{String(value).toUpperCase()}</Tag>,
    },
    { title: 'Bills', dataIndex: 'bills_count', key: 'bills_count', align: 'right' },
    { title: 'Billed Amount', dataIndex: 'billed_amount', key: 'billed_amount', align: 'right', render: formatCurrency },
    { title: 'Codes', dataIndex: 'generated_codes', key: 'generated_codes', align: 'right' },
    { title: 'Scanned Codes', dataIndex: 'scanned_codes', key: 'scanned_codes', align: 'right' },
    {
      title: 'Unscanned Codes',
      dataIndex: 'unscanned_codes',
      key: 'unscanned_codes',
      align: 'right',
      render: (v) => <span style={{ color: Number(v) ? '#cf1322' : undefined }}>{v}</span>,
    },
    { title: 'Scanned Amount', dataIndex: 'scanned_amount', key: 'scanned_amount', align: 'right', render: formatCurrency },
    {
      title: 'Unscanned Amount',
      dataIndex: 'unscanned_amount',
      key: 'unscanned_amount',
      align: 'right',
      render: (v) => <span style={{ color: Number(v) ? '#cf1322' : undefined }}>{formatCurrency(v)}</span>,
    },
  ];

  const paymentTotals = reportData.payment_breakdown.reduce(
    (acc, row) => {
      ['bills_count', 'billed_amount', 'generated_codes', 'scanned_codes', 'unscanned_codes', 'scanned_amount', 'unscanned_amount']
        .forEach((key) => { acc[key] += Number(row[key] || 0); });
      return acc;
    },
    { bills_count: 0, billed_amount: 0, generated_codes: 0, scanned_codes: 0, unscanned_codes: 0, scanned_amount: 0, unscanned_amount: 0 }
  );

  const scannedColumns = useMemo(() => [
    ...codeColumns,
    {
      title: 'Scanned At',
      dataIndex: 'verified_at',
      key: 'verified_at',
      width: 170,
      render: (value) => (value ? formatBangkokDateTime(value) : '-'),
    },
    { title: 'Scanned By', dataIndex: 'verified_by', key: 'verified_by', width: 110 },
  ], [codeColumns]);

  const filterByPayment = (rows) => (
    paymentFilter === 'all'
      ? rows
      : rows.filter((row) => String(row.payment_method || '').toLowerCase() === paymentFilter)
  );
  const visibleUnscanned = filterByPayment(unscannedTickets);
  const visibleScanned = filterByPayment(scannedTickets);
  const paymentOptions = Array.from(new Set(
    [...unscannedTickets, ...scannedTickets].map((row) => String(row.payment_method || 'unknown').toLowerCase())
  ));
  const sumAmount = (rows) => rows.reduce((total, row) => total + Number(row.ticket_amount || 0), 0);

  const paymentDailyColumns = [
    { title: 'Date', dataIndex: 'report_date', key: 'report_date', width: 120 },
    ...paymentColumns,
  ];

  const getExportData = () => ({
    summary,
    daily: reportData.daily,
    paymentBreakdown: reportData.payment_breakdown,
    paymentDaily: reportData.payment_daily,
    unscanned: unscannedTickets,
    scanned: scannedTickets,
  });
  const hasExportData = reportData.daily.length > 0 || unscannedTickets.length > 0 || scannedTickets.length > 0;

  const hasTableWarning = !reportData.table_status?.has_tracking_table || !reportData.table_status?.has_scan_logs_table;

  return (
    <Layout>
      <div className="container-fluid mt-3 mb-4">
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Card>
            <Row justify="space-between" align="middle" gutter={[16, 16]}>
              <Col>
                <Title level={4} style={{ margin: 0 }}>Ticket Reconciliation Report</Title>
                <Text type="secondary">Compare bills generated with gate scanner verification totals</Text>
              </Col>
              <Col>
                <Space wrap>
                  <RangePicker
                    value={dateRange}
                    onChange={(value) => setDateRange(value || [])}
                    allowClear={false}
                  />
                  {viewMode === 'reconciliation' ? (
                    <Button type="primary" onClick={() => loadAll(dateRange)} loading={loading}>
                      Load Report
                    </Button>
                  ) : (
                    <Button type="primary" onClick={() => fetchUnscannedTickets(dateRange)} loading={unscannedLoading}>
                      Load Details
                    </Button>
                  )}
                  <Button disabled={!hasExportData || loading || unscannedLoading} onClick={() => exportReconciliationPdf(getExportData(), dateRange)}>
                    Export PDF
                  </Button>
                  <Button disabled={!hasExportData || loading || unscannedLoading} onClick={() => exportReconciliationExcel(getExportData(), dateRange)}>
                    Export Excel
                  </Button>
                  <Button
                    type={viewMode === 'reconciliation' ? 'primary' : 'default'}
                    onClick={() => setViewMode('reconciliation')}
                  >
                    Reconciliation
                  </Button>
                  <Button
                    type={viewMode === 'unscanned' ? 'primary' : 'default'}
                    onClick={() => { setViewMode('unscanned'); fetchUnscannedTickets(dateRange); }}
                  >
                    <Badge
                      count={unscannedTickets.length}
                      showZero
                      style={{ backgroundColor: '#ff4d4f' }}
                    />
                    Unscanned / Code Difference
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {hasTableWarning && (
            <Alert
              type="warning"
              showIcon
              message="Gate tracking tables are missing"
              description="Please create ticket_barcode_tracking and gate_ticket_scan_logs tables to get full reconciliation results."
            />
          )}

          {error && (
            <Alert
              type="error"
              showIcon
              message="Unable to load reconciliation data"
              description={error}
            />
          )}

          {viewMode === 'reconciliation' ? (
          <Spin spinning={loading}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Billed Amount" value={summary.billed_amount} formatter={(v) => formatCurrency(v)} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Scanned Amount" value={summary.scanned_amount} formatter={(v) => formatCurrency(v)} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Amount Difference"
                    value={summary.amount_difference}
                    valueStyle={{ color: Number(summary.amount_difference) === 0 ? '#3f8600' : '#cf1322' }}
                    formatter={(v) => formatCurrency(v)}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Codes Difference"
                    value={summary.codes_difference}
                    valueStyle={{ color: Number(summary.codes_difference) === 0 ? '#3f8600' : '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Generated Codes" value={summary.generated_codes} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Unique Scanned Codes" value={summary.unique_scanned_codes} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Verified Scans" value={summary.verified_scans} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic title="Not Found Scans" value={summary.not_found_scans} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Generated but Unscanned"
                    value={summary.unscanned_codes || 0}
                    valueStyle={{ color: Number(summary.unscanned_codes) === 0 ? '#3f8600' : '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>
            <Alert
              style={{ margin: '16px 0' }}
              type="info"
              showIcon
              message="How scans are counted"
              description="Scans are counted against the ticket's business date (setup_date). A ticket billed before midnight and scanned after midnight is counted on the same business day as its bill."
            />

            <Card
              title="Payment-wise Reconciliation (Cash / Card / QR)"
              style={{ marginBottom: 16 }}
              extra={(
                <Radio.Group value={paymentView} onChange={(e) => setPaymentView(e.target.value)} size="small">
                  <Radio.Button value="total">Total</Radio.Button>
                  <Radio.Button value="daily">Per Day</Radio.Button>
                </Radio.Group>
              )}
            >
              {paymentView === 'daily' ? (
                reportData.payment_daily.length === 0 ? (
                  <Empty description="No payment data for selected date range" />
                ) : (
                  <Table
                    rowKey={(row) => `${row.report_date}-${row.payment_method}`}
                    columns={paymentDailyColumns}
                    dataSource={reportData.payment_daily}
                    pagination={{ pageSize: 20 }}
                    size="small"
                    scroll={{ x: 1000 }}
                  />
                )
              ) : reportData.payment_breakdown.length === 0 ? (
                <Empty description="No payment data for selected date range" />
              ) : (
                <Table
                  rowKey="payment_method"
                  columns={paymentColumns}
                  dataSource={reportData.payment_breakdown}
                  pagination={false}
                  size="small"
                  scroll={{ x: 900 }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right"><strong>{paymentTotals.bills_count}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><strong>{formatCurrency(paymentTotals.billed_amount)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right"><strong>{paymentTotals.generated_codes}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right"><strong>{paymentTotals.scanned_codes}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right"><strong>{paymentTotals.unscanned_codes}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right"><strong>{formatCurrency(paymentTotals.scanned_amount)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right"><strong>{formatCurrency(paymentTotals.unscanned_amount)}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              )}
            </Card>

            <Card title="Daily Reconciliation">
              {reportData.daily.length === 0 ? (
                <Empty description="No reconciliation records found for selected date range" />
              ) : (
                <Table
                  rowKey={(row) => row.report_date}
                  columns={columns}
                  dataSource={reportData.daily}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 1300 }}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right"><strong>{summary.bills_count || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><strong>{formatCurrency(summary.billed_amount)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right"><strong>{summary.generated_codes || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right"><strong>{summary.unique_scanned_codes || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right"><strong>{formatCurrency(summary.scanned_amount)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right"><strong>{formatCurrency(summary.amount_difference)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right"><strong>{summary.verified_scans || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right"><strong>{summary.duplicate_scans || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="right"><strong>{summary.not_found_scans || 0}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={10}>
                        {summary.is_amount_matched && summary.is_codes_matched ? (
                          <Tag color="success">Matched</Tag>
                        ) : (
                          <Tag color="error">Mismatch</Tag>
                        )}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              )}
            </Card>
          </Spin>
          ) : (
          <Spin spinning={unscannedLoading}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card>
                <Space wrap>
                  <Text strong>Payment type:</Text>
                  <Radio.Group value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
                    <Radio.Button value="all">All</Radio.Button>
                    {paymentOptions.map((method) => (
                      <Radio.Button key={method} value={method}>{method.toUpperCase()}</Radio.Button>
                    ))}
                  </Radio.Group>
                </Space>
              </Card>
              <Card
                title={`Generated but not scanned (${visibleUnscanned.length})`}
                extra={<Text strong>{formatCurrency(sumAmount(visibleUnscanned))}</Text>}
              >
                {visibleUnscanned.length === 0 ? (
                  <Empty description="No unscanned codes for the selected range / payment type" />
                ) : (
                  <Table
                    rowKey={(row) => row.barcode_value}
                    columns={codeColumns}
                    dataSource={visibleUnscanned}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 1100 }}
                  />
                )}
              </Card>
              <Card
                title={`Scanned codes (${visibleScanned.length})`}
                extra={<Text strong>{formatCurrency(sumAmount(visibleScanned))}</Text>}
              >
                {visibleScanned.length === 0 ? (
                  <Empty description="No scanned codes for the selected range / payment type" />
                ) : (
                  <Table
                    rowKey={(row) => row.barcode_value}
                    columns={scannedColumns}
                    dataSource={visibleScanned}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: 1400 }}
                  />
                )}
              </Card>
            </Space>
          </Spin>
          )}

          <Modal
            title={`Bill Details - ${selectedBillDetails?.bill_number || ''}`}
            visible={billDetailsModal}
            onCancel={() => setBillDetailsModal(false)}
            footer={[
              <Button key="close" onClick={() => setBillDetailsModal(false)}>
                Close
              </Button>,
            ]}
            width={700}
          >
            {selectedBillDetails && (
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <div><Text strong>Bill Number:</Text> {selectedBillDetails.bill_number}</div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div><Text strong>Setup Date:</Text> {selectedBillDetails.setup_date}</div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div><Text strong>Cashier:</Text> {selectedBillDetails.cashier_uname}</div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div><Text strong>Customer:</Text> {selectedBillDetails.customer_name}</div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div><Text strong>Total Amount:</Text> {formatCurrency(selectedBillDetails.total_amount)}</div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div><Text strong>Payment Method:</Text> {selectedBillDetails.payment_method}</div>
                  </Col>
                </Row>
                {selectedBillDetails.items && selectedBillDetails.items.length > 0 && (
                  <div>
                    <Text strong>Bill Items:</Text>
                    <Table
                      dataSource={selectedBillDetails.items}
                      columns={[
                        { title: 'Item', dataIndex: 'item_name', key: 'item_name' },
                        { title: 'Qty', dataIndex: 'quantity', key: 'quantity', align: 'right' },
                        { title: 'Price', dataIndex: 'unit_price', key: 'unit_price', align: 'right', render: (v) => formatCurrency(v) },
                        { title: 'Total', dataIndex: 'total_price', key: 'total_price', align: 'right', render: (v) => formatCurrency(v) },
                      ]}
                      pagination={false}
                      size="small"
                      rowKey={(row, idx) => idx}
                    />
                  </div>
                )}
              </Space>
            )}
          </Modal>
        </Space>
      </div>
    </Layout>
  );
}
